import { Col } from 'antd';
import { useFormikContext } from 'formik';
import { pick } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { type ApplicationMarketTypeFragmentFragment } from '../../../../api/fragments';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import validators from '../../../../utilities/validators';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type DealerMarketType = {
    coeEditable: boolean;
    ppsrEditable: boolean;
    estFeeEditable: boolean;
    bankEstFeeEditable: boolean;
    nzFeesViewable: boolean;
};

export const dealerMarketTypeValidations = (marketType: ApplicationMarketTypeFragmentFragment) =>
    validators.compose(
        validators.only(
            () => marketType?.__typename === 'SingaporeApplicationMarket',
            validators.requiredBoolean('dealerMarketType.coeEditable')
        ),
        validators.only(
            () => marketType?.__typename === 'NewZealandApplicationMarket',
            validators.compose(
                validators.requiredBoolean('dealerMarketType.nzFeesViewable'),
                validators.requiredBoolean('dealerMarketType.ppsrEditable'),
                validators.requiredBoolean('dealerMarketType.estFeeEditable'),
                validators.requiredBoolean('dealerMarketType.bankEstFeeEditable')
            )
        )
    );

export const getInitDealerMarketType = (marketType: ApplicationMarketTypeFragmentFragment, dealerId: string) => ({
    coeEditable:
        marketType?.__typename === 'SingaporeApplicationMarket'
            ? (marketType.coe?.overrides?.find(i => i.dealerId === dealerId)?.editable ?? marketType.coe?.editable)
            : false,

    ppsrEditable:
        marketType?.__typename === 'NewZealandApplicationMarket'
            ? (marketType.ppsr?.overrides?.find(i => i.dealerId === dealerId)?.editable ?? marketType.ppsr?.editable)
            : false,
    estFeeEditable:
        marketType?.__typename === 'NewZealandApplicationMarket'
            ? (marketType.estFee?.overrides?.find(i => i.dealerId === dealerId)?.editable ??
              marketType.estFee?.editable)
            : false,
    bankEstFeeEditable:
        marketType?.__typename === 'NewZealandApplicationMarket'
            ? (marketType.bankEstFee?.overrides?.find(i => i.dealerId === dealerId)?.editable ??
              marketType.bankEstFee?.editable)
            : false,
    nzFeesViewable:
        marketType?.__typename === 'NewZealandApplicationMarket'
            ? (marketType.nzFees?.overrides?.find(i => i.dealerId === dealerId)?.viewable ??
              marketType.nzFees?.viewable)
            : false,
});

export const pickDealerMarketType = (
    marketType: ApplicationMarketTypeFragmentFragment,
    dealerMarketType: DealerMarketType
) => {
    if (marketType?.__typename === 'SingaporeApplicationMarket') {
        return pick(['coeEditable'], dealerMarketType);
    }

    if (marketType?.__typename === 'NewZealandApplicationMarket') {
        return pick(['ppsrEditable', 'estFeeEditable', 'bankEstFeeEditable', 'nzFeesViewable'], dealerMarketType);
    }

    return {};
};

export type MarketTypeEditableFieldProps = {
    marketType: ApplicationMarketTypeFragmentFragment;
    disabled?: boolean;
};

const MarketTypeEditableField = ({ marketType, disabled }: MarketTypeEditableFieldProps) => {
    const { t } = useTranslation(['moduleDetails']);
    const { yesNoSwitch } = useSystemSwitchData();

    const { values } = useFormikContext<{ dealerMarketType: DealerMarketType }>();

    return (
        <>
            {marketType?.__typename === 'SingaporeApplicationMarket' && (
                <Col {...colSpan}>
                    <FormFields.SwitchField
                        {...t('moduleDetails:fields.coe.coeEditable', { returnObjects: true })}
                        {...yesNoSwitch}
                        disabled={disabled}
                        name="dealerMarketType.coeEditable"
                    />
                </Col>
            )}
            {marketType?.__typename === 'NewZealandApplicationMarket' && (
                <>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('moduleDetails:fields.nzFeesViewable', {
                                returnObjects: true,
                            })}
                            {...yesNoSwitch}
                            disabled={disabled}
                            name="dealerMarketType.nzFeesViewable"
                        />
                    </Col>
                    {values.dealerMarketType.nzFeesViewable && (
                        <>
                            <Col {...colSpan}>
                                <FormFields.SwitchField
                                    {...t('moduleDetails:fields.ppsr.ppsrEditable', {
                                        returnObjects: true,
                                    })}
                                    {...yesNoSwitch}
                                    disabled={disabled}
                                    name="dealerMarketType.ppsrEditable"
                                />
                            </Col>
                            <Col {...colSpan}>
                                <FormFields.SwitchField
                                    {...t('moduleDetails:fields.estFee.estFeeEditable', {
                                        returnObjects: true,
                                    })}
                                    {...yesNoSwitch}
                                    disabled={disabled}
                                    name="dealerMarketType.estFeeEditable"
                                />
                            </Col>
                            <Col {...colSpan}>
                                <FormFields.SwitchField
                                    {...t('moduleDetails:fields.bankEstFeeEditable', {
                                        returnObjects: true,
                                    })}
                                    {...yesNoSwitch}
                                    disabled={disabled}
                                    name="dealerMarketType.bankEstFeeEditable"
                                />
                            </Col>
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default MarketTypeEditableField;
